/**
 * ChatThread — `@assistant-ui/react` 의 headless primitives 위에 우리 디자인
 * 토큰(`var(--pk-*)`) 으로 입힌 공유 채팅 UI.
 *
 * 책임:
 *  - AssistantRuntimeProvider 로 runtime 주입
 *  - ThreadPrimitive.Root → Viewport → Messages → MessagePrimitive.Parts 의
 *    primitives 트리 구성 (className 으로 우리 토큰 적용)
 *  - tool-call part 는 기본 ToolCallCard, 호스트가 toolUI 매핑을 주면 toolName
 *    별 커스텀 카드로 우선
 *  - ComposerPrimitive 로 입력창 + Send/Cancel 버튼 — 한국어 placeholder
 *  - emptyHint: thread 가 비었을 때 보여줄 안내 영역
 *  - hideComposer: 읽기 전용 모드
 *
 * 절대 제약(plan):
 *  - assistant-cloud 절대 import 금지 — 본 파일은 client/cloud 모듈을 참조하지 않는다
 *  - 모든 색/공간/라인은 `var(--pk-*)` 만 사용 — 호스트가 자기 토큰 정의
 *
 * 호스트 페이지가 주입할 책임:
 *  - runtime: useBatchRuntime 또는 다른 useExternalStoreRuntime 결과
 *  - placeholder / emptyHint / toolUI 등 옵션
 */
import {
	type AssistantRuntime,
	AssistantRuntimeProvider,
	ComposerPrimitive,
	MessagePrimitive,
	ThreadPrimitive,
	type ToolCallMessagePartComponent
} from "@assistant-ui/react"
import type { ReactNode } from "react"
import { ToolCallCard } from "./ToolCallCard.tsx"
import type { ToolUIMap } from "./types.ts"

/**
 * ChatThread props.
 */
export interface ChatThreadProps {
	/** Batch runtime — 보통 useBatchRuntime 결과의 `runtime` 을 넘긴다. */
	readonly runtime: AssistantRuntime
	/** 입력창 placeholder. 기본 "메시지를 입력하세요…". */
	readonly placeholder?: string | undefined
	/** thread 가 비었을 때 표시할 안내 영역 (인사 / suggestion). */
	readonly emptyHint?: ReactNode | undefined
	/** true 면 입력창을 숨김 (읽기 전용). 기본 false. */
	readonly hideComposer?: boolean | undefined
	/**
	 * true 면 입력창을 disabled 처리 (사용자 입력 차단). 기본 false.
	 *
	 * `hideComposer` 와 달리 입력창 영역은 보존하면서 사용자에게 "현재 입력 불가" 상태를
	 * 시각적으로 알린다. 예: 서비스 미선택 시 placeholder 만 변경하면서 disabled 로 전환.
	 *
	 * `hideComposer === true` 가 우선이며 그 경우 disabled 는 의미 없다.
	 */
	readonly disabledComposer?: boolean | undefined
	/**
	 * toolName → 커스텀 카드 매핑.
	 * 매핑에 없는 toolName 은 기본 ToolCallCard 가 fallback 으로 처리.
	 */
	readonly toolUI?: ToolUIMap | undefined
	/** 추가 className (호스트 페이지에서 상위 레이아웃 클래스 부여). */
	readonly className?: string | undefined
	/**
	 * 에이전트 응답 진행 중 여부 — true 면 viewport 안에 typing indicator 가 노출된다.
	 * 보통 `useBatchRuntime` 의 `isRunning` 을 그대로 전달한다.
	 */
	readonly isRunning?: boolean | undefined
}

/**
 * 공유 채팅 컴포넌트.
 *
 * 사용 예:
 * ```tsx
 * const { runtime } = useBatchRuntime({ storageKey: "pk.x", fetcher: ... })
 * return (
 *   <ChatThread
 *     runtime={runtime}
 *     placeholder="에이전트에게 질문하세요"
 *     emptyHint={<div>새 대화를 시작하세요</div>}
 *     toolUI={{ suggest_slice: SliceCard }}
 *   />
 * )
 * ```
 */
export function ChatThread(props: ChatThreadProps) {
	const placeholder = props.placeholder ?? "메시지를 입력하세요…"
	const rootClass = `pk-chat${props.className ? ` ${props.className}` : ""}`
	const disabled = props.disabledComposer === true

	return (
		<AssistantRuntimeProvider runtime={props.runtime}>
			<ThreadPrimitive.Root className={rootClass}>
				<ThreadPrimitive.Viewport className="pk-chat__viewport">
					<ThreadPrimitive.Empty>
						<div className="pk-chat__empty">{props.emptyHint}</div>
					</ThreadPrimitive.Empty>
					<ThreadPrimitive.Messages
						components={{
							UserMessage: UserMessage,
							AssistantMessage: () => <AssistantMessage toolUI={props.toolUI} />
						}}
					/>
					{props.isRunning === true && (
						<output className="pk-chat__typing-indicator" aria-live="polite">
							<span className="pk-chat__typing-dot" aria-hidden />
							<span className="pk-chat__typing-dot" aria-hidden />
							<span className="pk-chat__typing-dot" aria-hidden />
							<span className="pk-chat__typing-text">에이전트 응답 중…</span>
						</output>
					)}
				</ThreadPrimitive.Viewport>
				{!props.hideComposer && (
					<ComposerPrimitive.Root
						className={`pk-chat__composer${disabled ? " pk-chat__composer--disabled" : ""}`}
					>
						<ComposerPrimitive.Input
							className="pk-chat__composer-input"
							placeholder={placeholder}
							disabled={disabled}
						/>
						<ComposerPrimitive.Send className="pk-chat__composer-send" aria-label="전송">
							보내기
						</ComposerPrimitive.Send>
						<ComposerPrimitive.Cancel className="pk-chat__composer-cancel" aria-label="취소">
							취소
						</ComposerPrimitive.Cancel>
					</ComposerPrimitive.Root>
				)}
			</ThreadPrimitive.Root>
		</AssistantRuntimeProvider>
	)
}

/** 사용자 메시지 — 우측 정렬 버블. */
function UserMessage() {
	return (
		<MessagePrimitive.Root className="pk-chat__msg pk-chat__msg--user">
			<div className="pk-chat__msg-bubble">
				<MessagePrimitive.Parts />
			</div>
		</MessagePrimitive.Root>
	)
}

/**
 * 에이전트 메시지 — 좌측 정렬, tool-call part 는 커스텀/기본 카드로 렌더.
 *
 * @param props 메시지 정보 + 호스트가 주입한 toolUI 매핑
 */
function AssistantMessage(props: { readonly toolUI?: ToolUIMap | undefined }) {
	return (
		<MessagePrimitive.Root className="pk-chat__msg pk-chat__msg--assistant">
			<div className="pk-chat__msg-bubble">
				<MessagePrimitive.Parts
					components={{
						tools: {
							by_name: props.toolUI ? buildToolByNameMap(props.toolUI) : undefined,
							Fallback: DefaultToolPart
						}
					}}
				/>
			</div>
		</MessagePrimitive.Root>
	)
}

/**
 * 호스트 toolUI 매핑(우리 prop 시그니처) 을 assistant-ui 의
 * `Record<toolName, ToolCallMessagePartComponent>` 로 변환.
 *
 * 우리 ToolCallRenderProps 와 라이브러리 ToolCallMessagePartProps 의 공통 필드만
 * 추출해 위임 — 라이브러리가 추가 필드(addResult/resume 등) 를 넘겨도 호스트
 * 컴포넌트는 무시한다.
 */
function buildToolByNameMap(toolUI: ToolUIMap): Record<string, ToolCallMessagePartComponent> {
	const out: Record<string, ToolCallMessagePartComponent> = {}
	for (const [name, Component] of Object.entries(toolUI)) {
		out[name] = (lib) => (
			<Component
				toolCallId={lib.toolCallId}
				toolName={lib.toolName}
				args={lib.args}
				result={lib.result}
				isError={lib.isError}
				status={mapStatus(lib.status)}
			/>
		)
	}
	return out
}

/** 매핑에 없는 toolName 은 기본 ToolCallCard 로 fallback. */
const DefaultToolPart: ToolCallMessagePartComponent = (lib) => (
	<ToolCallCard
		toolCallId={lib.toolCallId}
		toolName={lib.toolName}
		args={lib.args}
		result={lib.result}
		isError={lib.isError}
		status={mapStatus(lib.status)}
	/>
)

/**
 * 라이브러리 ToolCallMessagePartStatus → 우리 단순화 status 로 매핑.
 *
 * "requires-action" / "running" → "in_progress",
 * "complete" → "complete",
 * "incomplete" → "error".
 */
function mapStatus(status: { readonly type: string }): "in_progress" | "complete" | "error" {
	if (status.type === "complete") return "complete"
	if (status.type === "incomplete") return "error"
	return "in_progress"
}
